import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import {
  Send,
  FileCode,
  StickyNote,
  X,
  Paperclip,
  Loader2,
  Mic,
  MicOff,
  StopCircle,
  MessageCircle,
  Pencil,
  Code,
  Globe,
  Zap,
  ZapOff,
  Bot,
  ChevronUp,
  ChevronDown,
  AtSign,
  FolderOpen,
  Library,
  Wand2,
  Check,
  Plus,
  Search,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './Button';
import { Textarea } from './Textarea';
import { Badge } from './Badge';
import { AnnotationSummary } from './AiChatAnnotationSelector';
import { useVoiceInput } from '../helpers/useVoiceInput';
import { useWebSearch } from '../helpers/useWebSearch';
import { useMentions, parseMessage } from '../helpers/useMentions';
import { usePresenceCentralized } from '../helpers/usePresenceApi';
import { PresenceParticipant } from '../endpoints/presence/list_GET.schema';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';
import { WebSearchResults, SearchResult } from './WebSearchResults';
import { ModelSelector, ModelSelection } from './ModelSelector';
import { ConductorDialog } from './ConductorDialog';
import { AiConfigDialog } from './AiConfigDialog';
import { PersonaSelector } from './PersonaSelector';
import { MentionSuggestions } from './MentionSuggestions';
import { ChatAttachmentsLibrary } from './ChatAttachmentsLibrary';
import { uploadChatAttachment } from '../endpoints/chat-attachments/upload_POST.schema';
import { uploadChatAttachmentDirect } from '../endpoints/chat-attachments/upload-direct_POST.schema';
import { ChatAttachmentItem } from '../endpoints/chat-attachments/list_GET.schema';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './Dialog';
import { GlbViewer, isGlbFile } from './GlbViewer';
import styles from './AiChatInput.module.css';
import type { InstalledSkill } from './SkillSearchModal';
import { loadInstalledSkills, saveInstalledSkills } from './SkillSearchModal';

import { SLASH_COMMANDS } from '../helpers/slashCommands';

interface AiChatInputProps {
  onSend: (
    message: string,
    context: string[],
    attachment?: { content: string; name: string; addToProject?: boolean },
    webSearchResults?: SearchResult[],
  ) => Promise<void>;
  onDirectMessage?: (
    message: string,
    targetUserIds: number[],
    mentions: Array<{ userId?: number; displayName?: string }>,
  ) => Promise<void>;
  disabled?: boolean;
  currentFile?: string;
  annotations?: AnnotationSummary[];
  sessionId: string;
  onStopGeneration?: () => void;
  initialAttachment?: { content: string; name?: string; textNotes?: string[] } | null;
  onRequestAnnotateAttachment?: (dataUrl: string) => void;
  onSaveImageToFiles?: (dataUrl: string, filename?: string) => Promise<string>; // Save image to project files
  mode?: 'generate' | 'chat' | 'engineer';
  onModeChange?: (mode: 'generate' | 'chat' | 'engineer') => void;
  webSearchEnabled?: boolean;
  onWebSearchToggle?: () => void;
  autoExecute?: boolean;
  onAutoExecuteChange?: (enabled: boolean) => void;
  selectedModel?: ModelSelection;
  onModelChange?: (model: ModelSelection | undefined) => void;
  isOwner?: boolean;
  hasQueuedMessages?: boolean; // Show indicator when messages are queued
  processingCount?: number; // Number of messages currently being processed
  participants?: PresenceParticipant[]; // Participants for @mentions
  activeSkills?: InstalledSkill[]; // Currently active skills attached to chat
  onActiveSkillsChange?: (skills: InstalledSkill[]) => void; // Callback when skills toggled
  onOpenSkillSearch?: (query?: string) => void; // Open skill search modal
  autoStartVoice?: boolean; // Auto-start voice input (e.g. when AI meeting starts)
  onVoiceInterrupt?: () => void; // Called when user starts speaking — silences AI voices
}

const AUTOSAVE_KEY_PREFIX = 'ai-chat-draft-';
const HISTORY_KEY_PREFIX = 'ai-chat-history-';
const MAX_HISTORY_SIZE = 50;

// ─── Skills dropdown with search ───
function SkillsDropdownPanel({
  allInstalledSkills,
  onToggleSkill,
  onOpenSkillSearch,
}: {
  allInstalledSkills: InstalledSkill[];
  onToggleSkill: (skill: InstalledSkill) => void;
  onOpenSkillSearch?: () => void;
}) {
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => { searchRef.current?.focus(); }, []);

  const filtered = query.trim()
    ? allInstalledSkills.filter(s =>
        s.name.toLowerCase().includes(query.toLowerCase()) ||
        (s.description || '').toLowerCase().includes(query.toLowerCase())
      )
    : allInstalledSkills;

  return (
    <div className={styles.skillsDropdown}>
      <div className={styles.skillsDropdownHeader}>
        <span>Skills</span>
        <div style={{ flex: 1, margin: '0 6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--background)', fontSize: '0.78rem' }}>
            <Search size={11} style={{ color: 'var(--muted-foreground)', flexShrink: 0 }} />
            <input
              ref={searchRef}
              type="text"
              placeholder="Search skills..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ border: 'none', outline: 'none', background: 'transparent', color: 'inherit', fontSize: 'inherit', width: '100%', padding: 0 }}
            />
          </div>
        </div>
        {onOpenSkillSearch && (
          <button className={styles.skillsDropdownFind} onClick={onOpenSkillSearch} title="Find & install more skills">
            <Plus size={12} /> Install
          </button>
        )}
      </div>
      {filtered.length === 0 ? (
        <div className={styles.skillsDropdownEmpty}>
          {allInstalledSkills.length === 0 ? (
            <>No skills installed. {onOpenSkillSearch && <button className={styles.skillsDropdownLink} onClick={onOpenSkillSearch}>Browse skills</button>}</>
          ) : (
            <>No skills matching "{query}"</>
          )}
        </div>
      ) : (
        <div className={styles.skillsDropdownList}>
          {filtered.map((skill) => {
            const isActive = !!skill.active;
            return (
              <label key={skill.name} className={`${styles.skillsDropdownItem} ${isActive ? styles.skillsDropdownItemActive : ''}`}>
                <input
                  id={`skill-toggle-${skill.name}`}
                  name={`skill-toggle-${skill.name}`}
                  type="checkbox"
                  checked={isActive}
                  onChange={() => onToggleSkill(skill)}
                  className={styles.skillCheckbox}
                />
                <div className={styles.skillItemInfo}>
                  <span className={styles.skillItemName}>{skill.name}</span>
                  <span className={styles.skillItemDesc}>{skill.description?.slice(0, 60)}{(skill.description?.length || 0) > 60 ? '...' : ''}</span>
                </div>
                {isActive && <Check size={14} className={styles.skillCheckIcon} />}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

export const AiChatInput: React.FC<AiChatInputProps> = ({
  onSend,
  onDirectMessage,
  disabled = false,
  currentFile,
  annotations,
  sessionId,
  onStopGeneration,
  initialAttachment,
  onRequestAnnotateAttachment,
  onSaveImageToFiles,
  mode = 'generate',
  onModeChange,
  webSearchEnabled = false,
  onWebSearchToggle,
  autoExecute = true,
  onAutoExecuteChange,
  selectedModel,
  onModelChange,
  isOwner = false,
  hasQueuedMessages = false,
  processingCount = 0,
  participants: propParticipants,
  activeSkills = [],
  onActiveSkillsChange,
  onOpenSkillSearch,
  autoStartVoice = false,
  onVoiceInterrupt,
}) => {
  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [includeContext, setIncludeContext] = useState(false); // Default to false - don't auto-select files
  const [selectedAnnotationIds, setSelectedAnnotationIds] = useState<string[]>([]);

  const [attachment, setAttachmentRaw] = useState<string | null>(null);
  const [attachmentName, setAttachmentName] = useState<string | null>(null);
  const [addToProject, setAddToProject] = useState(false);
  const [annotationTextNotes, setAnnotationTextNotes] = useState<string[]>([]);
  const [isSavingToFiles, setIsSavingToFiles] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingFileRef = useRef<File | null>(null); // Raw File for direct binary upload
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [isSkillsDropdownOpen, setIsSkillsDropdownOpen] = useState(false);
  const skillsDropdownRef = useRef<HTMLDivElement>(null);
  const [skillsVersion, setSkillsVersion] = useState(0);

  // Mute completion sound toggle (persisted in localStorage)
  const [muteCompletion, setMuteCompletion] = useState(() => {
    try { return localStorage.getItem('tasking_mute_completion') === 'true'; } catch { return false; }
  });
  const handleMuteToggle = useCallback(() => {
    setMuteCompletion((prev) => {
      const next = !prev;
      try { localStorage.setItem('tasking_mute_completion', String(next)); } catch {}
      return next;
    });
  }, []);

  // All installed skills for the dropdown — refreshes on dropdown toggle or after any change
  const allInstalledSkills = useMemo(
    () => loadInstalledSkills(),
    [isSkillsDropdownOpen, skillsVersion],
  );

  // Close skills dropdown on outside click
  useEffect(() => {
    if (!isSkillsDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (skillsDropdownRef.current && !skillsDropdownRef.current.contains(e.target as Node)) {
        setIsSkillsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSkillsDropdownOpen]);

  // Toggle a skill's active state globally (persists across sessions)
  const handleToggleSkill = useCallback(
    (skill: InstalledSkill) => {
      const allSkills = loadInstalledSkills();
      const updated = allSkills.map((s) =>
        s.name === skill.name ? { ...s, active: !s.active } : s,
      );
      saveInstalledSkills(updated);
      setSkillsVersion((v) => v + 1);
      onActiveSkillsChange?.(updated.filter((s) => s.active));
    },
    [onActiveSkillsChange],
  );

  // Handler to select attachment from library
  const handleSelectFromLibrary = useCallback((item: ChatAttachmentItem) => {
    setAttachmentFromLibrary(true); // Mark as from library to skip auto-save
    setAttachmentRaw(item.dataUrl);
    setAttachmentName(item.fileName);
    setIsLibraryOpen(false);
    toast.success(`Attached: ${item.fileName}`);
  }, []);

  // Auto-save attachment to library when one is attached
  // Uses direct binary upload for large files (>4MB) to avoid Worker CPU limits
  const DIRECT_UPLOAD_THRESHOLD = 4 * 1024 * 1024; // 4MB

  const saveAttachmentToLibrary = useCallback(
    async (dataUrl: string, fileName: string) => {
      try {
        const fileType = dataUrl.split(';')[0]?.split(':')[1] || 'application/octet-stream';
        const base64Length = dataUrl.split(',')[1]?.length || 0;
        const fileSize = Math.round((base64Length * 3) / 4);

        // Use direct binary upload for large files to bypass Worker CPU limits
        if (fileSize > DIRECT_UPLOAD_THRESHOLD && pendingFileRef.current) {
          await uploadChatAttachmentDirect(pendingFileRef.current, {
            sessionId,
            fileName,
            fileType,
          });
          console.log('[AiChatInput] Large file uploaded directly to R2:', fileName, `(${(fileSize / 1024 / 1024).toFixed(1)}MB)`);
          pendingFileRef.current = null;
        } else {
          await uploadChatAttachment({
            sessionId,
            fileName,
            fileType,
            fileSize,
            dataUrl,
          });
          console.log('[AiChatInput] Attachment saved to library:', fileName);
        }
      } catch (e) {
        console.error('[AiChatInput] Failed to save attachment to library:', e);
      }
    },
    [sessionId],
  );

  // Handler to save image to project files
  const handleSaveToFiles = useCallback(async () => {
    if (!attachment || !onSaveImageToFiles) return;

    setIsSavingToFiles(true);
    try {
      const savedPath = await onSaveImageToFiles(attachment, attachmentName || undefined);
      toast.success(`Image saved to ${savedPath}`);
    } catch (e) {
      console.error('[AiChatInput] Failed to save image to files:', e);
      toast.error('Failed to save image to files');
    } finally {
      setIsSavingToFiles(false);
    }
  }, [attachment, attachmentName, onSaveImageToFiles]);

  // Wrapper to validate attachment is actual base64 image data
  const setAttachment = useCallback((value: string | null) => {
    if (value === null) {
      setAttachmentRaw(null);
      return;
    }
    // Accept data URLs and R2-stored asset URLs
    if (typeof value === 'string' && (value.startsWith('data:') || value.startsWith('https://') || value.startsWith('/'))) {
      setAttachmentRaw(value);
      console.log('[AiChatInput] Valid attachment set:', value.length, 'chars');
    } else {
      console.error('[AiChatInput] Rejected invalid attachment:', value?.substring(0, 100));
      toast.error(
        'Invalid file data. Please try again or use the attach button.',
      );
      setAttachmentRaw(null);
    }
  }, []);

  // Track if current attachment came from library (skip auto-save)
  const [attachmentFromLibrary, setAttachmentFromLibrary] = useState(false);

  // Auto-save attachment to library when one is attached (but not if it came from library)
  useEffect(() => {
    if (attachment && attachmentName && !attachmentFromLibrary) {
      saveAttachmentToLibrary(attachment, attachmentName);
    }
    // Reset the fromLibrary flag when attachment changes
    if (!attachment) {
      setAttachmentFromLibrary(false);
    }
  }, [attachment, attachmentName, attachmentFromLibrary, saveAttachmentToLibrary]);

  // Web search state - controlled from parent via webSearchEnabled prop
  const [pendingSearchResults, setPendingSearchResults] = useState<SearchResult[]>([]);
  const {
    isSearching,
    results: searchResults,
    query: searchQuery,
    search,
    clearResults,
  } = useWebSearch();

  // Message history for up/down arrow navigation
  const [messageHistory, setMessageHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const historyIndexRef = useRef(-1);
  const draftBeforeHistoryRef = useRef<string | null>(null);

  // Presence and mentions
  const { data: presenceData } = usePresenceCentralized(sessionId);
  const participants = propParticipants ?? presenceData ?? [];

  const {
    mentionSuggestions,
    selectedMentionIndex,
    setSelectedMentionIndex,
    detectMention,
    completeMention,
    handleMentionKeyDown,
    resetMention,
    isShowingMentions,
  } = useMentions(participants);

  // Slash command suggestions
  const [slashSuggestions, setSlashSuggestions] = useState<(typeof SLASH_COMMANDS)[number][]>([]);
  const [selectedSlashIndex, setSelectedSlashIndex] = useState(0);
  const isShowingSlash = slashSuggestions.length > 0;

  // Detect slash commands
  const detectSlashCommand = useCallback((text: string, cursorPos: number) => {
    // Only detect if cursor is at the start or after a space/newline
    const beforeCursor = text.slice(0, cursorPos);
    const slashMatch = beforeCursor.match(/(?:^|\s)(\/\w*)$/);

    if (slashMatch) {
      const query = slashMatch[1].toLowerCase();

      if (query === '/') {
        setSlashSuggestions(SLASH_COMMANDS);
        setSelectedSlashIndex(0);
        return;
      }

      const matches = SLASH_COMMANDS.filter((cmd) => {
        if (cmd.command.toLowerCase().startsWith(query)) return true;
        return cmd.aliases?.some((alias) => alias.toLowerCase().startsWith(query));
      });

      if (matches.length > 0) {
        setSlashSuggestions(matches);
        setSelectedSlashIndex(0);
      } else {
        setSlashSuggestions([]);
      }
    } else {
      setSlashSuggestions([]);
    }
  }, []);

  // Complete a slash command
  const completeSlashCommand = useCallback(
    (cmd: (typeof SLASH_COMMANDS)[number]) => {
      const cursorPos = textareaRef.current?.selectionStart || input.length;
      const beforeCursor = input.slice(0, cursorPos);
      const afterCursor = input.slice(cursorPos);

      // Find and replace the partial slash command
      const slashMatch = beforeCursor.match(/(?:^|\s)(\/\w*)$/);
      if (slashMatch) {
        const startPos = beforeCursor.length - slashMatch[1].length;
        const newText = input.slice(0, startPos) + cmd.command + ' ' + afterCursor;
        setInput(newText);
        setSlashSuggestions([]);

        // Focus and position cursor after command
        setTimeout(() => {
          if (textareaRef.current) {
            const newPos = startPos + cmd.command.length + 1;
            textareaRef.current.selectionStart = newPos;
            textareaRef.current.selectionEnd = newPos;
            textareaRef.current.focus();
          }
        }, 0);
      }
    },
    [input],
  );

  // Track cursor position for mention and slash detection
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      const cursorPos = e.target.selectionStart || 0;
      setInput(newValue);
      detectMention(newValue, cursorPos);
      detectSlashCommand(newValue, cursorPos);
    },
    [detectMention, detectSlashCommand],
  );

  // Complete a mention when selected
  const handleSelectMention = useCallback(
    (participant: PresenceParticipant) => {
      const cursorPos = textareaRef.current?.selectionStart || input.length;
      const { newText, newCursorPosition } = completeMention(input, participant, cursorPos);
      setInput(newText);

      // Set cursor position after React re-renders
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.selectionStart = newCursorPosition;
          textareaRef.current.selectionEnd = newCursorPosition;
          textareaRef.current.focus();
        }
      }, 0);
    },
    [input, completeMention],
  );

  // If a parent supplies an initial attachment (e.g. annotated image), apply it
  useEffect(() => {
    if (initialAttachment && initialAttachment.content) {
      setAttachment(initialAttachment.content);
      setAttachmentName(initialAttachment.name || 'screenshot.png');
      // Store any text notes from the annotation so they're included in the message
      if (initialAttachment.textNotes?.length) {
        setAnnotationTextNotes(initialAttachment.textNotes);
      }
    }
  }, [initialAttachment]);

  const { isListening, transcript, isSupported, startListening, stopListening, resetTranscript } =
    useVoiceInput({
      onAutoSend: async (message: string) => {
        const base = inputBeforeListening.current || '';
        const combined = base ? `${base} ${message}` : message;
        setInput(combined);
        resetTranscript();
        // Automatically send the message when voice auto-sends
        try {
          await sendMessage(combined);
        } catch (err) {
          console.error('Failed to send voice message:', err);
        }
      },
    });

  // We need a ref to store the text before listening started
  const inputBeforeListening = useRef('');

  // Auto-start voice input when requested (e.g. AI meeting started)
  const prevAutoStart = useRef(false);
  useEffect(() => {
    if (autoStartVoice && !prevAutoStart.current && isSupported && !isListening) {
      resetTranscript();
      startListening();
    }
    if (!autoStartVoice && prevAutoStart.current && isListening) {
      stopListening();
    }
    prevAutoStart.current = autoStartVoice;
  }, [autoStartVoice, isSupported, isListening, startListening, stopListening, resetTranscript]);

  // Listen for tasking.prefillChat — prefills input from live preview element inspector or plan cards
  useEffect(() => {
    const handler = (e: Event) => {
      const { message, autoSend, files } = (e as CustomEvent).detail ?? {};
      if (!message) return;
      setInput(message);

      // Attach image files from element inspector (upload action)
      if (files && files.length > 0 && files[0].dataUrl) {
        setAttachment(files[0].dataUrl);
        setAttachmentName(files[0].name || 'element_image.png');
      }

      if (autoSend) {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('tasking.autoSendChat', { detail: { message } }));
        }, 60);
      } else {
        setTimeout(() => {
          const ta = textareaRef.current;
          if (ta) {
            ta.focus();
            ta.setSelectionRange(message.length, message.length);
          }
        }, 30);
      }
    };
    window.addEventListener('tasking.prefillChat', handler);
    return () => window.removeEventListener('tasking.prefillChat', handler);
  }, [setAttachment]);

  // Listen for tasking.autoSendChat — auto-sends a message (from plan card option clicks)
  useEffect(() => {
    const handler = (e: Event) => {
      const { message } = (e as CustomEvent).detail ?? {};
      if (message) sendMessage(message);
    };
    window.addEventListener('tasking.autoSendChat', handler);
    return () => window.removeEventListener('tasking.autoSendChat', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load draft and history from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(AUTOSAVE_KEY_PREFIX + sessionId);
    if (saved) setInput(saved);

    const history = localStorage.getItem(HISTORY_KEY_PREFIX + sessionId);
    if (history) {
      try {
        const parsed = JSON.parse(history);
        if (Array.isArray(parsed)) {
          setMessageHistory(parsed);
        }
      } catch (e) {
        console.error('Failed to parse message history:', e);
      }
    }
  }, [sessionId]);

  // Autosave draft to localStorage (debounced to avoid excessive writes)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        // Avoid overwriting an existing saved draft with an empty string.
        if (input.trim().length > 0) {
          localStorage.setItem(AUTOSAVE_KEY_PREFIX + sessionId, input);
        }
      } catch (e) {
        console.error('Failed to save draft:', e);
      }
    }, 300); // Debounce by 300ms

    return () => clearTimeout(timeoutId);
  }, [input, sessionId]);

  // Save message history to localStorage
  const saveHistory = (history: string[]) => {
    try {
      localStorage.setItem(HISTORY_KEY_PREFIX + sessionId, JSON.stringify(history));
    } catch (e) {
      console.error('Failed to save message history:', e);
    }
  };

  // Autocomplete removed — no suggestion logic.

  // When voice listening stops (manually), append the transcript to the input but don't auto-send
  useEffect(() => {
    if (!isListening && transcript) {
      const base = inputBeforeListening.current || '';
      const combined = base ? `${base} ${transcript}` : transcript;
      setInput(combined);
      resetTranscript();
      // Note: Auto-sending is now handled by the onAutoSend callback in useVoiceInput
    }
  }, [isListening, transcript, resetTranscript]);

  // Unified send that can be used by button or voice transcript
  const sendMessage = async (messageOverride?: string) => {
    let message = (typeof messageOverride === 'string' ? messageOverride : input || '').trim();
    if (!message && attachment) {
      message = `[Image attached: ${attachmentName}]`;
    }

    if (!message) return;

    // Add message to history (avoid duplicates)
    setMessageHistory((prev) => {
      const filtered = prev.filter((m) => m !== message);
      const updated = [message, ...filtered].slice(0, MAX_HISTORY_SIZE);
      saveHistory(updated);
      return updated;
    });
    setHistoryIndex(-1);
    historyIndexRef.current = -1;

    const context: string[] = [];
    if (includeContext && currentFile) {
      context.push(currentFile);
    }

    // Add annotation context from selected annotations
    if (annotations && selectedAnnotationIds.length > 0) {
      const selectedAnnotations = annotations.filter((a) => selectedAnnotationIds.includes(a.id));
      const annotationContexts = selectedAnnotations.map((ann) => {
        const notes = ann.annotationData?.textNotes?.map((n: any) => n.text).join('; ') || '';
        return `[Annotation Context]\nDescription: ${ann.description || 'Untitled'}\nNotes: ${notes || 'No text notes'}`;
      });
      context.push(...annotationContexts);
    }

    // Add text notes from current annotation attachment (drawn text bubbles + description)
    if (annotationTextNotes.length > 0 && attachment) {
      context.push(`[Annotation Notes on Attached Screenshot]\n${annotationTextNotes.join('\n')}`);
    }

    // Perform web search if enabled
    let searchResultsToSend: SearchResult[] = [];
    if (webSearchEnabled) {
      const searchResponse = await search(message);
      if (searchResponse) {
        searchResultsToSend = searchResponse.results;
        setPendingSearchResults(searchResponse.results);
      }
    }

    await onSend(
      message,
      context,
      attachment && attachmentName
        ? { content: attachment, name: attachmentName, addToProject: addToProject || undefined }
        : undefined,
      searchResultsToSend.length > 0 ? searchResultsToSend : undefined,
    );

    // Clear input, attachment, text notes, and search results after successful send
    setInput('');
    setAttachment(null);
    setAttachmentName(null);
    setAddToProject(false);
    setAnnotationTextNotes([]);
    setPendingSearchResults([]);
    clearResults();

    // Clear draft from localStorage after successful send
    try {
      localStorage.removeItem(AUTOSAVE_KEY_PREFIX + sessionId);
    } catch (e) {
      // ignore
    }
  };

  const handleVoiceToggle = () => {
    if (isListening) {
      // Stop listening when clicked while active
      stopListening();
    } else {
      // Operator takes the mic — silence all AI voices immediately
      onVoiceInterrupt?.();
      // Capture current input before starting
      inputBeforeListening.current = input;
      resetTranscript();
      startListening();
    }
  };

  const handleAttachmentClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Store raw File for direct binary upload (used for large files)
    pendingFileRef.current = file;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = ev.target?.result as string;
      setAttachment(result);
      setAttachmentName(file.name);
    };
    // read as data URL so images can preview and other files can be uploaded as data URLs
    reader.readAsDataURL(file);

    // Reset input so same file can be selected again
    e.target.value = '';
  };

  // Drag & drop handlers - allow users to drop image files or image URLs into the chat
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  };

  const handleDragLeave = (_e: React.DragEvent) => {
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const dt = e.dataTransfer;

    // If files were dropped, prefer them
    if (dt && dt.files && dt.files.length > 0) {
      const file = dt.files[0];
      pendingFileRef.current = file; // Store raw File for direct binary upload
      const reader = new FileReader();
      reader.onload = (ev) => {
        const result = ev.target?.result as string;
        if (result) {
          // For images, use setAttachment (shows preview)
          if (result.startsWith('data:image/')) {
            setAttachment(result);
            let fileName = file.name || 'dropped-image.png';
            if (fileName.includes('/') || fileName.includes('\\')) {
              fileName = fileName.split(/[/\\]/).pop() || fileName;
            }
            setAttachmentName(fileName);
            toast.success('Image attached');
            console.log('[AiChatInput] Image dropped and attached:', fileName);
          } else {
            // For non-image files, still attach them
            setAttachmentRaw(result);
            let fileName = file.name || 'dropped-file';
            if (fileName.includes('/') || fileName.includes('\\')) {
              fileName = fileName.split(/[/\\]/).pop() || fileName;
            }
            setAttachmentName(fileName);
            toast.success(`File attached: ${fileName}`);
            console.log('[AiChatInput] File dropped and attached:', fileName, file.type);
          }
        } else {
          toast.error('Failed to read dropped file');
        }
      };
      reader.onerror = (err) => {
        console.error('[AiChatInput] Failed to read dropped file:', err);
        toast.error('Failed to read dropped file');
      };
      reader.readAsDataURL(file);
      return;
    }

    // If no file, check for a text/URL (dragging image from another tab or desktop may give a URL)
    const url = dt?.getData('text/uri-list') || dt?.getData('text/plain');
    if (url && url.startsWith('http')) {
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);
        const blob = await response.blob();
        if (!blob.type.startsWith('image/')) {
          toast.error('Dropped URL did not point to an image');
          return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
          const result = ev.target?.result as string;
          if (result && result.startsWith('data:image/')) {
            setAttachment(result);
            const fileName = url.split('/').pop()?.split('?')[0] || 'image.png';
            setAttachmentName(fileName);
            toast.success('Image attached from URL');
            console.log('[AiChatInput] Image fetched from URL and attached:', url);
          }
        };
        reader.readAsDataURL(blob);
      } catch (err) {
        console.error('[AiChatInput] Failed to fetch dropped URL image:', err);
        toast.error('Failed to fetch image from dropped URL');
      }
    }
  };

  const removeAttachment = () => {
    setAttachment(null);
    setAttachmentName(null);
    setAddToProject(false);
  };

  const handleSend = async () => {
    let message = input.trim();
    if (!message && attachment) {
      message = `[Image attached: ${attachmentName}]`;
    }

    if (!message) return;

    // Reset mentions
    resetMention();

    // Parse message for @mentions
    const parsedMessage = parseMessage(message, participants);

    // Add message to history (avoid duplicates)
    setMessageHistory((prev) => {
      const filtered = prev.filter((m) => m !== message);
      const updated = [message, ...filtered].slice(0, MAX_HISTORY_SIZE);
      saveHistory(updated);
      return updated;
    });
    setHistoryIndex(-1);
    historyIndexRef.current = -1;

    // Clear input IMMEDIATELY when Enter is pressed (better UX)
    // Capture attachment before clearing
    const currentAttachment = attachment;
    const currentAttachmentName = attachmentName;
    setInput('');
    setAttachment(null);
    setAttachmentName(null);

    // Clear draft from localStorage immediately
    try {
      localStorage.removeItem(AUTOSAVE_KEY_PREFIX + sessionId);
    } catch (e) {
      // ignore
    }

    // Handle direct messages (bypass AI)
    if (
      parsedMessage.type === 'direct' &&
      onDirectMessage &&
      parsedMessage.targetUserIds.length > 0
    ) {
      await onDirectMessage(
        parsedMessage.cleanedText || message,
        parsedMessage.targetUserIds,
        parsedMessage.mentions.map((m) => ({
          userId: m.userId,
          displayName: m.displayName,
        })),
      );
      return;
    }

    const context: string[] = [];

    if (includeContext && currentFile) {
      context.push(currentFile);
    }

    // Add annotation context
    if (annotations && selectedAnnotationIds.length > 0) {
      const selectedAnnotations = annotations.filter((a) => selectedAnnotationIds.includes(a.id));
      const annotationContexts = selectedAnnotations.map((ann) => {
        const notes = ann.annotationData?.textNotes?.map((n: any) => n.text).join('; ') || '';
        return `[Annotation Context]
Description: ${ann.description || 'Untitled'}
Notes: ${notes || 'No text notes'}`;
      });
      context.push(...annotationContexts);
    }

    // Perform web search if enabled
    let searchResultsToSend: SearchResult[] = [];
    if (webSearchEnabled) {
      const searchResponse = await search(message);
      if (searchResponse) {
        searchResultsToSend = searchResponse.results;
        setPendingSearchResults(searchResponse.results);
      }
    }

    // Only send attachment if it's valid base64 image data
    const validAttachment =
      currentAttachment &&
      currentAttachmentName &&
      typeof currentAttachment === 'string' &&
      (currentAttachment.startsWith('data:') || currentAttachment.startsWith('https://') || currentAttachment.startsWith('/'))
        ? {
            content: currentAttachment,
            name: currentAttachmentName,
            addToProject: addToProject || undefined,
          }
        : undefined;

    if (currentAttachment && !validAttachment) {
      console.error(
        '[AiChatInput] Invalid attachment:',
        currentAttachment?.substring(0, 100),
      );
      toast.error('Attachment failed. Please use the attach button to upload.');
    }

    await onSend(
      message,
      context,
      validAttachment,
      searchResultsToSend.length > 0 ? searchResultsToSend : undefined,
    );

    // Reset "Use in project" toggle and search results after send
    setAddToProject(false);
    setPendingSearchResults([]);
    clearResults();
  };

  // Navigate to previous (older) message in history
  const navigateHistoryUp = () => {
    if (messageHistory.length === 0) return;

    let newIndex = historyIndexRef.current;

    // When starting history navigation, capture current draft
    if (newIndex === -1) {
      draftBeforeHistoryRef.current = input;
    }
    // Move up in history (older messages)
    if (newIndex < messageHistory.length - 1) {
      newIndex = newIndex + 1;
    } else if (newIndex === -1) {
      newIndex = 0;
    }

    historyIndexRef.current = newIndex;
    setHistoryIndex(newIndex);
    setInput(messageHistory[newIndex]);
  };

  // Navigate to next (newer) message in history
  const navigateHistoryDown = () => {
    if (messageHistory.length === 0) return;

    let newIndex = historyIndexRef.current;

    if (newIndex > 0) {
      newIndex = newIndex - 1;
      historyIndexRef.current = newIndex;
      setHistoryIndex(newIndex);
      setInput(messageHistory[newIndex]);
    } else if (newIndex === 0) {
      newIndex = -1;
      historyIndexRef.current = newIndex;
      setHistoryIndex(newIndex);
      const draft = draftBeforeHistoryRef.current ?? '';
      setInput(draft);
      draftBeforeHistoryRef.current = null;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle slash command navigation first
    if (isShowingSlash) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedSlashIndex((i) => Math.min(i + 1, slashSuggestions.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedSlashIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Tab' || e.key === 'Enter') {
        e.preventDefault();
        const selectedCmd = slashSuggestions[selectedSlashIndex];
        if (selectedCmd) {
          completeSlashCommand(selectedCmd);
        }
        return;
      }
      if (e.key === 'Escape') {
        setSlashSuggestions([]);
        return;
      }
    }

    // Handle mention navigation
    if (isShowingMentions) {
      if (handleMentionKeyDown(e)) {
        // Tab or Enter completes the mention
        if (e.key === 'Tab' || e.key === 'Enter') {
          const selectedParticipant = mentionSuggestions[selectedMentionIndex];
          if (selectedParticipant) {
            handleSelectMention(selectedParticipant);
          }
        }
        return;
      }
    }

    // Up/Down arrow keys for message history (only when not showing mentions or slash)
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      if (isShowingMentions || isShowingSlash) return; // Let other handlers take care of it
      if (messageHistory.length === 0) return;

      e.preventDefault();

      if (e.key === 'ArrowUp') {
        navigateHistoryUp();
      } else {
        navigateHistoryDown();
      }

      return;
    }

    // Enter sends the message (unless Shift or Ctrl is held)
    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      handleSend();
      return;
    }
    // Ctrl+Enter or Shift+Enter creates a new line (default behavior, no preventDefault needed)
  };

  // Handle paste for clipboard images
  const handlePaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    const text = e.clipboardData?.getData('text/plain');

    // PRIORITY 1: Check for actual image data in clipboard items FIRST
    // This handles normal copy-paste of images from browsers, screenshots, etc.
    if (items) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            const reader = new FileReader();
            reader.onload = (ev) => {
              const result = ev.target?.result as string;
              // Validate that we got a proper data URL, not a file path
              if (result && result.startsWith('data:image/')) {
                setAttachment(result);
                // Clean up filename - remove path components
                let fileName = file.name || 'pasted-image.png';
                if (fileName.includes('/') || fileName.includes('\\')) {
                  fileName = fileName.split(/[/\\]/).pop() || 'pasted-image.png';
                }
                setAttachmentName(fileName);
                toast.success('Image attached');
                console.log(
                  '[AiChatInput] Image pasted successfully:',
                  fileName,
                  result.length,
                  'chars',
                );
              } else {
                console.error(
                  '[AiChatInput] Invalid image data - expected data URL, got:',
                  result?.substring(0, 100),
                );
              }
            };
            reader.onerror = (err) => {
              console.error('[AiChatInput] Failed to read pasted image:', err);
            };
            reader.readAsDataURL(file);
          }
          return; // Found image data, exit early
        }
      }
    }

    // PRIORITY 2: If no image data found, check for file path (common in Electron/Pinokio)
    if (text && (text.includes('.pinokio-temp') || text.match(/\.(png|jpg|jpeg|gif|webp)$/i))) {
      // Always prevent the raw file path from being pasted as text
      e.preventDefault();
      const filePath = text.trim();
      const cleanName = filePath.split(/[/\\]/).pop() || 'pasted-image.png';
      console.log('[AiChatInput] Detected file path in clipboard:', filePath);

      // Try multiple approaches to get the image data
      let loaded = false;

      // Approach 1: Try to fetch the image via local URL (Pinokio serves temp files)
      if (!loaded) {
        try {
          // Pinokio temp files are accessible at the same origin
          const fetchUrl = filePath.startsWith('/') ? filePath : `/${filePath}`;
          const resp = await fetch(fetchUrl);
          if (resp.ok && resp.headers.get('content-type')?.startsWith('image/')) {
            const blob = await resp.blob();
            const dataUrl = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result as string);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
            if (dataUrl.startsWith('data:image/')) {
              setAttachment(dataUrl);
              setAttachmentName(cleanName);
              toast.success('Image attached');
              console.log('[AiChatInput] Image loaded via fetch:', cleanName);
              loaded = true;
            }
          }
        } catch (fetchErr) {
          console.log('[AiChatInput] Fetch approach failed:', fetchErr);
        }
      }

      // Approach 2: Try Clipboard API read() for actual file data
      if (!loaded) {
        try {
          const clipboardItems = await navigator.clipboard.read();
          for (const item of clipboardItems) {
            for (const type of item.types) {
              if (type.startsWith('image/')) {
                const blob = await item.getType(type);
                const dataUrl = await new Promise<string>((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onload = () => resolve(reader.result as string);
                  reader.onerror = reject;
                  reader.readAsDataURL(blob);
                });
                if (dataUrl.startsWith('data:image/')) {
                  setAttachment(dataUrl);
                  setAttachmentName(cleanName);
                  toast.success('Image attached');
                  console.log('[AiChatInput] Image loaded via Clipboard API:', cleanName);
                  loaded = true;
                  break;
                }
              }
            }
            if (loaded) break;
          }
        } catch (clipboardErr) {
          console.log('[AiChatInput] Clipboard API read failed:', clipboardErr);
        }
      }

      // Approach 3: Try Pinokio/Electron IPC if available
      if (!loaded && (window as any).electronAPI?.readFile) {
        try {
          const fileData = await (window as any).electronAPI.readFile(filePath);
          if (fileData) {
            setAttachment(fileData);
            setAttachmentName(cleanName);
            toast.success('Image attached');
            loaded = true;
          }
        } catch (electronErr) {
          console.log('[AiChatInput] Electron IPC failed:', electronErr);
        }
      }

      // Approach 4: Try pterm command if in Pinokio environment
      if (!loaded && (window as any).pterm?.clipboard?.read) {
        try {
          const imgData = await (window as any).pterm.clipboard.read();
          if (imgData && imgData.startsWith('data:image/')) {
            setAttachment(imgData);
            setAttachmentName(cleanName);
            toast.success('Image attached');
            loaded = true;
          }
        } catch (ptermErr) {
          console.log('[AiChatInput] Pterm clipboard failed:', ptermErr);
        }
      }

      // If all approaches fail, show helpful message
      if (!loaded) {
        toast.error('Could not load image from clipboard. Use the attach button instead.', {
          duration: 4000,
        });
      }
      return;
    }
  };

  return (
    <div className={styles.inputArea}>
      {selectedAnnotationIds.length > 0 && annotations && (
        <div className={styles.chipContainer}>
          {selectedAnnotationIds.map((id) => {
            const ann = annotations.find((a) => a.id === id);
            if (!ann) return null;
            return (
              <Badge key={id} variant="secondary" className={styles.chip}>
                <StickyNote size={10} style={{ marginRight: 4 }} />
                <span className={styles.chipText}>{ann.description || 'Annotation'}</span>
                <button
                  className={styles.removeChip}
                  onClick={() => setSelectedAnnotationIds((ids) => ids.filter((i) => i !== id))}
                  type="button"
                >
                  <X size={10} />
                </button>
              </Badge>
            );
          })}
        </div>
      )}

      {/* Web search results display */}
      {webSearchEnabled && (isSearching || pendingSearchResults.length > 0) && (
        <div className={styles.searchResultsContainer}>
          <WebSearchResults
            results={pendingSearchResults}
            isLoading={isSearching}
            query={searchQuery || undefined}
          />
        </div>
      )}

      {attachment && (
        <div className={styles.attachmentPreview}>
          <div className={styles.previewThumbWrapper}>
            {isGlbFile(attachmentName || '') || isGlbFile(attachment) ? (
              <GlbViewer src={attachment} name={attachmentName || undefined} compact />
            ) : attachment?.startsWith('data:image/') ? (
              <img src={attachment} alt="Attachment" className={styles.previewThumb} />
            ) : (
              <div className={styles.filePlaceholder}>
                <FileCode size={18} />
              </div>
            )}
            {/* Save to files - uploads image to /public/images/ */}
            {typeof onSaveImageToFiles === 'function' && attachment?.startsWith('data:image/') && (
              <button
                className={styles.annotateAttachment}
                onClick={handleSaveToFiles}
                type="button"
                title="Save image to project files"
                disabled={isSavingToFiles}
                style={{ left: onRequestAnnotateAttachment ? '46px' : '24px' }}
              >
                {isSavingToFiles ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <FolderOpen size={12} />
                )}
              </button>
            )}
            {/* Annotate attachment - opens annotation dialog with this image */}
            {typeof onRequestAnnotateAttachment === 'function' && attachment && (
              <button
                className={styles.annotateAttachment}
                onClick={() => onRequestAnnotateAttachment(attachment)}
                type="button"
                title="Annotate attachment"
              >
                <Pencil size={12} />
              </button>
            )}
            <button className={styles.removeAttachment} onClick={removeAttachment} type="button">
              <X size={10} />
            </button>
          </div>
          <span className={styles.attachmentName}>{attachmentName}</span>
          {/* "Use in project" toggle — for images and 3D models, not annotations */}
          {(attachment?.startsWith('data:image/') || isGlbFile(attachmentName || '') || isGlbFile(attachment || '')) && annotationTextNotes.length === 0 && (
            <label className={styles.addToProjectToggle}>
              <input
                id="add-to-project"
                name="add-to-project"
                type="checkbox"
                checked={addToProject}
                onChange={(e) => setAddToProject(e.target.checked)}
              />
              <span>Use in project</span>
            </label>
          )}
          {annotationTextNotes.length > 0 && (
            <span className={styles.attachmentNotes} title={annotationTextNotes.join(' | ')}>
              {annotationTextNotes.length} note{annotationTextNotes.length > 1 ? 's' : ''} attached
            </span>
          )}
        </div>
      )}

      <div className={styles.inputWrapper}>
        <input
          id="chat-file-upload"
          name="chat-file-upload"
          type="file"
          ref={fileInputRef}
          className={styles.hiddenInput}
          accept="*/*"
          onChange={handleFileChange}
        />

        {/* THREE-PART LAYOUT: Top toolbar → Textarea → Bottom toolbar */}
        <div
          className={styles.twoToolbarLayout}
          style={{
            display: 'grid',
            gridTemplateRows: 'auto auto auto',
            gridTemplateColumns: '1fr',
            gap: '4px',
            width: '100%',
          }}
        >
          {/* ROW 1 - TOP TOOLBAR */}
          <div
            className={styles.topToolbar}
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '2px',
              background: 'var(--muted)',
              borderRadius: '6px',
              padding: '4px 6px',
              minHeight: '32px',
            }}
          >
            <Button
              size="icon-sm"
              variant="ghost"
              onClick={handleAttachmentClick}
              className={styles.toolbarBtn}
              title="Attach file"
            >
              <Paperclip size={14} />
            </Button>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon-sm"
                  variant="ghost"
                  onClick={() => setIsLibraryOpen(true)}
                  className={styles.toolbarBtn}
                  title="Open attachments library"
                >
                  <Library size={14} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>My Attachments Library</TooltipContent>
            </Tooltip>

            {/* Skills attachment button */}
            <div style={{ position: 'relative' }} ref={skillsDropdownRef}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon-sm"
                    variant={activeSkills.length > 0 ? 'primary' : 'ghost'}
                    onClick={() => setIsSkillsDropdownOpen((v) => !v)}
                    className={styles.toolbarBtn}
                    title="Attach skills to chat"
                  >
                    <Wand2 size={14} />
                    {activeSkills.length > 0 && (
                      <span className={styles.skillBadgeCount}>{activeSkills.length}</span>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {activeSkills.length > 0
                    ? `${activeSkills.length} skill${activeSkills.length > 1 ? 's' : ''} attached`
                    : 'Attach skills to chat'}
                </TooltipContent>
              </Tooltip>

              {isSkillsDropdownOpen && (
                <SkillsDropdownPanel
                  allInstalledSkills={allInstalledSkills}
                  onToggleSkill={handleToggleSkill}
                  onOpenSkillSearch={onOpenSkillSearch ? () => { setIsSkillsDropdownOpen(false); onOpenSkillSearch!(); } : undefined}
                />
              )}
            </div>

            {onAutoExecuteChange && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon-sm"
                    variant={autoExecute ? 'primary' : 'ghost'}
                    onClick={() => onAutoExecuteChange(!autoExecute)}
                    className={styles.toolbarBtn}
                  >
                    {autoExecute ? <Zap size={14} /> : <ZapOff size={14} />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {autoExecute ? 'Auto-Execute ON' : 'Auto-Execute OFF'}
                </TooltipContent>
              </Tooltip>
            )}

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon-sm"
                  variant={muteCompletion ? 'ghost' : 'ghost'}
                  onClick={handleMuteToggle}
                  className={styles.toolbarBtn}
                >
                  {muteCompletion ? <VolumeX size={14} /> : <Volume2 size={14} />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {muteCompletion ? 'Completion sound OFF' : 'Completion sound ON'}
              </TooltipContent>
            </Tooltip>

          </div>

          {/* Active Skills Display */}
          {activeSkills.length > 0 && (
            <div className={styles.activeSkillsBar}>
              {activeSkills.map((skill) => (
                <button
                  key={skill.name}
                  className={styles.skillPill}
                  onClick={(e) => {
                    e.preventDefault();
                    handleToggleSkill(skill);
                  }}
                  title={`Remove ${skill.name}`}
                >
                  <span className={styles.skillPillIcon}>✨</span>
                  <span className={styles.skillPillName}>{skill.name}</span>
                  <span className={styles.skillPillClose}>×</span>
                </button>
              ))}
            </div>
          )}

          {/* ROW 2 - TEXTAREA with history navigation arrows */}
          <div className={styles.textareaRow}>
            <div className={styles.textareaContainer}>
              <Textarea
                ref={textareaRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onDragOver={handleDragOver}
                onDragEnter={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                id="ai-chat-input"
                name="ai-chat-input"
                placeholder="Ask anything... (@ to mention, Enter to send)"
                className={`${styles.mainTextarea} ${isDragOver ? styles.dragOverInput : ''}`}
                rows={2}
                autoComplete="off"
              />
              {/* Slash command suggestions popup */}
              {isShowingSlash && (
                <div className={styles.mentionPopup}>
                  <div className={styles.slashCommands}>
                    {slashSuggestions.map((cmd, idx) => (
                      <div
                        key={cmd.command}
                        className={`${styles.slashItem} ${idx === selectedSlashIndex ? styles.slashItemSelected : ''}`}
                        onClick={() => completeSlashCommand(cmd)}
                        onMouseEnter={() => setSelectedSlashIndex(idx)}
                      >
                        <div className={styles.slashMeta}>
                          <span className={styles.slashCommand}>{cmd.command}</span>
                          {cmd.aliases && cmd.aliases.length > 0 && (
                            <span className={styles.slashAlias}>{cmd.aliases.join(', ')}</span>
                          )}
                        </div>
                        <span className={styles.slashDesc}>{cmd.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Mention suggestions popup */}
              {isShowingMentions && (
                <div className={styles.mentionPopup}>
                  <MentionSuggestions
                    suggestions={mentionSuggestions}
                    selectedIndex={selectedMentionIndex}
                    onSelect={handleSelectMention}
                    onHover={setSelectedMentionIndex}
                  />
                </div>
              )}
            </div>
            {/* Mobile history navigation arrows */}
            <div className={styles.historyArrows}>
              <button
                type="button"
                className={styles.historyArrowBtn}
                onClick={navigateHistoryUp}
                disabled={messageHistory.length === 0}
                title="Previous message"
              >
                <ChevronUp size={16} />
              </button>
              <button
                type="button"
                className={styles.historyArrowBtn}
                onClick={navigateHistoryDown}
                disabled={historyIndex === -1}
                title="Next message"
              >
                <ChevronDown size={16} />
              </button>
            </div>
          </div>

          {/* ROW 3 - BOTTOM TOOLBAR */}
          <div
            className={styles.bottomToolbar}
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '4px',
              background: 'var(--muted)',
              borderRadius: '6px',
              padding: '4px 6px',
              minHeight: '32px',
              justifyContent: 'space-between',
            }}
          >
            <div className={styles.bottomLeft}>
              {/* MODEL SELECTOR - Cloud/Local toggle + model dropdown */}
              {onModelChange && (
                <ModelSelector
                  value={selectedModel}
                  onChange={(m) => onModelChange(m)}
                  className={styles.modelSelector}
                />
              )}
            </div>

            <div className={styles.bottomRight}>
              {isSupported && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="icon-sm"
                      variant={isListening ? 'destructive' : 'ghost'}
                      onClick={handleVoiceToggle}
                      disabled={!isSupported || disabled}
                      className={styles.toolbarBtn}
                    >
                      {isListening ? <MicOff size={14} /> : <Mic size={14} />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {isListening
                      ? 'Click to stop listening'
                      : 'Voice input (auto-sends after 1s silence)'}
                  </TooltipContent>
                </Tooltip>
              )}

              {onModeChange && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="icon-sm"
                      variant={mode === 'chat' ? 'primary' : 'ghost'}
                      onClick={() => {
                        onModeChange(mode === 'chat' ? 'generate' : 'chat');
                      }}
                      className={styles.toolbarBtn}
                    >
                      {mode === 'chat' ? <MessageCircle size={14} /> : <Code size={14} />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{mode === 'chat' ? 'Chat mode' : 'Code mode'}</TooltipContent>
                </Tooltip>
              )}

              {/* Stop button - separate, always visible when AI is generating */}
              {onStopGeneration && (
                <Button
                  size="icon-sm"
                  onClick={onStopGeneration}
                  variant="destructive"
                  className={styles.stopBtn}
                  title="Stop generation"
                >
                  <StopCircle size={14} />
                </Button>
              )}

              {/* Globe, Crown, Gear — grouped next to Send */}
              {onWebSearchToggle && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="icon-sm"
                      variant={webSearchEnabled ? 'primary' : 'ghost'}
                      onClick={onWebSearchToggle}
                      className={styles.toolbarBtn}
                    >
                      <Globe size={14} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {webSearchEnabled ? 'Web search ON' : 'Web search OFF'}
                  </TooltipContent>
                </Tooltip>
              )}
              <PersonaSelector sessionId={sessionId} />
              <ConductorDialog sessionId={sessionId} disabled={disabled} />
              <AiConfigDialog sessionId={sessionId} />

              {/* Send button - always visible */}
              <Button
                size="icon-sm"
                onClick={handleSend}
                disabled={(!input.trim() && !attachment) || disabled}
                variant="primary"
                className={`${styles.sendBtn} ${processingCount > 0 ? styles.sendBtnQueued : ''}`}
                title={
                  processingCount > 0 ? `Add to queue (${processingCount} processing)` : 'Send'
                }
              >
                {disabled ? (
                  <Loader2 size={14} className={styles.spinner} />
                ) : processingCount > 0 ? (
                  <div className={styles.queueIcon}>
                    <Send size={14} />
                    <span className={styles.queueBadge}>+</span>
                  </div>
                ) : (
                  <Send size={14} />
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Chat Attachments Library Panel */}
      <ChatAttachmentsLibrary
        sessionId={sessionId}
        isOpen={isLibraryOpen}
        onClose={() => setIsLibraryOpen(false)}
        onSelectAttachment={handleSelectFromLibrary}
      />
    </div>
  );
};
